import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { AmbientestModuleContextType, BasicProviderProps, UserDocumentFile, FormEnviroment, SarEnvProfile, ApiSaenvProfileResp } from '../types';
import ErrorBoundary from '../components/error/ErrorBoundary';
import useDataClient from '../hooks/useDataClient';
import { AppContext } from './AppContext';
import { sarenvAdapter } from '../adapter/sarsetting.adapter';
import { DashboardContext } from './DashboardContext';
import ModalEnvResponse from '../components/modal/ModalEnvResponse';
import useCatFederalStates from '../hooks/useCatFederalStates';
import useCurpDataCache from '../hooks/useCurpDataCache';
import { GAHelper } from '../helpers/ga.helper';

export const AmbientestModuleContext = createContext<AmbientestModuleContextType | null>(null);

interface AmbientestModuleProviderProps extends BasicProviderProps {
    formFields: FormEnviroment,
    path: string
}

export default function AmbientestModuleProvider({ children, path, formFields }: Readonly<AmbientestModuleProviderProps>) {
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const [userData, setUserData] = useState<SarEnvProfile>()
    const [userDocuments, setUserDocuments] = useState<UserDocumentFile[]>([])
    const [curpBaseData, setCurpBaseData] = useCurpDataCache()
    const sarenvProfileClient = useDataClient<ApiSaenvProfileResp>({ appContext });
    const envType = useMemo(() => {
        let pathsFragment = path.split('/') || [];
        return pathsFragment[pathsFragment.length - 1];
    }, [path])
    const federalStates = useCatFederalStates()
    const steps = useMemo<string[]>(() => {
        let baseSteps = ['Validación de CURP', 'Datos del trabajador']
        if (formFields.requestSelfie || formFields.requestIdDocument || (formFields.requestAdditionalDocument?.length || 0) > 0) {
            baseSteps = [...baseSteps, 'Documentos del trabajador']
        }
        return baseSteps
    }, [formFields])

    function validateCurp(curp: string) {
        setCurpBaseData(curp)
    }

    function setEnvProfile(data: SarEnvProfile) {
        setUserData(data)
    }

    function clearContext() {
        sarenvProfileClient.clean()
        setCurpBaseData('')
        setUserData(undefined)
        setUserDocuments([])
    }

    const contextData = useMemo<AmbientestModuleContextType>(() => ({
        steps,
        userData,
        setUserDocuments,
        validateCurp,
        setEnvProfile,
        formFields,
        path,
        federalStates,
        clearContext,
        requestUserEnviroment: () => {
            if (!userData) {
                appContext?.setAppDialog({ message: "Datos del trabajador faltantes", type: 'error' })
                return
            }
            appContext?.setLoading(true)
            sarenvAdapter.enviromentProfile(`${formFields.businessLayer}/${envType}`, userData, userDocuments).then(adapter => {
                GAHelper.setEnvRequest(envType, userData.aforeCode, userData.name);
                sarenvProfileClient.executeRequest(adapter)
            }).catch(() => {
                appContext?.setAppDialog({ message: "Problema al procesar la información", type: 'error' })
            }).finally(() => {
                appContext?.setLoading(false)
            })
        },
        afores: dashboardContext?.getCatalog('AFO')?.filter(a => a.value !== '*') || [],
    }), [envType, formFields, userData, userDocuments])

    useEffect(() => {
        if (curpBaseData) {
            setUserData(curpBaseData)
        }
    }, [curpBaseData])

    useEffect(() => {
        if (sarenvProfileClient.status.data) {
            GAHelper.setEnvResponse(envType, userData?.aforeCode, userData?.name);
        }
    }, [sarenvProfileClient.status.data, envType])

    return <ErrorBoundary>
        <AmbientestModuleContext.Provider value={contextData}>
            {children}
        </AmbientestModuleContext.Provider>
        <ModalEnvResponse curp={userData?.curp} data={sarenvProfileClient.status.data} onClose={clearContext} />
    </ErrorBoundary>
}
